import React, { useState, useEffect } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import Toast from "../../../../components/Toast";
import { JobPosting } from "./JobsList";
import SkillsSection from "./SkillsSection";
import { Job, JobSkill } from "../model/JobModel";
import {
  useJobCreateViewModel,
  useJobsListViewModel,
} from "../viewmodel/job.viewmodel";
import {
  updateFormField,
  addSkill,
  deleteSkill,
  setNewSkill,
  setShowModal,
  setToast,
  hideToast,
  setFormErrors,
  updateSkill,
} from "../viewmodel/job.action";
import {
  EducationLevel,
  ExperienceLevel,
  HiringUrgency,
  JobStatus,
  JobType,
  PostingVisibility,
  salaryCurrency,
  SalaryPeriod,
  WorkMode,
} from "../model/jobEnums";
import { createJob, getJobsDetails, updateJobData } from "../api/job_api";
import { validateJobForm } from "../viewmodel/job.validation";
import InputField from "../../../../components/InputFieldForm";
import SelectdDropDown from "../../../../components/DropDownComponent";
import TextAreaField from "../../../../components/TextAreaComponent";
import { PendingApprovalsPage } from "./PendingApprovalsPage";
import { ApprovalPopup } from "../Components/ApprovalJobPopUp";
import { useLocation } from "react-router-dom";

const JobManagement: React.FC = () => {
  const {
    state: jobState,
    dispatch: jobDispatch,
    fetchApprovals,
    fetchSubmittedApprovals,
  } = useJobCreateViewModel();

  const { state, loadUsers, fetchJobs } = useJobsListViewModel();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
    fetchApprovals();
  }, []);

  const JobTypeLabelMap: Record<JobType, string> = {
    [JobType.FullTime]: "Full Time",
    [JobType.PartTime]: "Part Time",
    [JobType.Contract]: "Contract",
    [JobType.Temporary]: "Temporary",
    [JobType.Internship]: "Internship",
  };
  const ExperienceLeveMap: Record<ExperienceLevel, string> = {
    [ExperienceLevel.Entry]: "Entry",
    [ExperienceLevel.Executive]: "Executive",
    [ExperienceLevel.Lead]: "Lead",
    [ExperienceLevel.Mid]: "Mid",
    [ExperienceLevel.Senior]: "Senior",
  };
  const WorkModeMap: Record<WorkMode, string> = {
    [WorkMode.Hybrid]: "Hybrid",
    [WorkMode.Remote]: "Remote",
    [WorkMode.OnSite]: "OnSite",
  };
  const SalaryPeriodMap: Record<SalaryPeriod, string> = {
    [SalaryPeriod.Annual]: "Annual",
    [SalaryPeriod.Hourly]: "Hourly",
    [SalaryPeriod.Monthly]: "Monthly",
  };
  const HiringUrgencyMap: Record<HiringUrgency, string> = {
    [HiringUrgency.Flexible]: "Flexible",
    [HiringUrgency.Immediate]: "Immediate",
    [HiringUrgency.Within30Days]: "Within30Days",
    [HiringUrgency.Within60Days]: "Within60Days",
  };
  const EducationLevelMap: Record<EducationLevel, string> = {
    [EducationLevel.HighSchool]: "HighSchool",
    [EducationLevel.Associate]: "Associate",
    [EducationLevel.Bachelors]: "Bachelors",
    [EducationLevel.Masters]: "Masters",
    [EducationLevel.PhD]: "PhD",
    [EducationLevel.None]: "None",
  };
  const PostingVisibilityLabelMap: Record<PostingVisibility, string> = {
    [PostingVisibility.InternalOnly]: "Internal Only",
    [PostingVisibility.ExternalOnly]: "External Only",
    [PostingVisibility.Both]: "Both",
  };
  // Helper functions

  const handleUpdateFormField = (field: keyof Job, value: any) =>
    jobDispatch(updateFormField(field, value));
  const handleAddSkill = () => {
    if (!jobState.newSkill.skill_name.trim()) return;

    const skillWithId: JobSkill = {
      ...jobState.newSkill,
      skill_id: Date.now().toString(),
    };

    jobDispatch(addSkill(skillWithId));

    jobDispatch(
      setNewSkill({
        skill_name: "",
        is_required: false,
        minimum_years: 0,
        proficiency_level: "beginner",
      }),
    );
  };

  const handleDeleteSkill = (skillId: string) =>
    jobDispatch(deleteSkill(skillId));
  const handleUpdateSkill = (skillId: string, updates: Partial<JobSkill>) => {
    jobDispatch(updateSkill(skillId, updates));
  };
  const showToastMessage = (
    message: string,
    type: "success" | "error" | "warning" | "info",
  ) => {
    jobDispatch(setToast({ isVisible: true, message, type }));
    setToastInstanceKey((prev) => prev + 1);
  };
  const hideToastMessage = () => jobDispatch(hideToast());
  const [toastInstanceKey, setToastInstanceKey] = useState(0);

  const setShowModalValue = (show: boolean) => jobDispatch(setShowModal(show));

  const buildJobPayload = () => {
    const formatDateToISO = (dateString?: string | null) => {
      if (!dateString?.trim()) return null;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date.toISOString();
    };

    return {
      title: jobState.formData.title?.trim(),
      hiring_manager_id: jobState.formData.hiring_manager_name,
      recruiter_id: jobState.formData.recruiter_name || null,

      slug: jobState.formData.title
        ?.toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-"),

      description: jobState.formData.description?.trim() || "N/A",
      requirements: jobState.formData.requirements || null,
      responsibilities: jobState.formData.responsibilities || null,
      benefits: jobState.formData.benefits || null,

      job_type: jobState.formData.job_type,
      work_mode: jobState.formData.work_mode,
      experience_level: jobState.formData.experience_level,

      department: jobState.formData.department || null,
      job_function: jobState.formData.job_function || null,
      location: jobState.formData.location || null,

      salary_min: Number(jobState.formData.salary_min || 0),
      salary_max: Number(jobState.formData.salary_max || 0),
      salary_currency: jobState.formData.salary_currency,
      salary_period: jobState.formData.salary_period,

      education_level: jobState.formData.education_level || null,
      vacancies: Number(jobState.formData.vacancies || 1),

      visa_sponsorship: Boolean(jobState.formData.visa_sponsorship),
      relocation_offered: Boolean(jobState.formData.relocation_offered),
      travel_percentage: Number(jobState.formData.travel_percentage || 0),

      target_start_date: formatDateToISO(jobState.formData.target_start_date),
      application_deadline: formatDateToISO(
        jobState.formData.application_deadline,
      ),

      hiring_urgency: jobState.formData.hiring_urgency,
      interview_process: jobState.formData.interview_process || null,

      external_url: jobState.formData.external_url || null,
      application_email: jobState.formData.application_email || null,

      max_applications: Number(jobState.formData.max_applications || 1),
      posting_visibility: jobState.formData.posting_visibility,

      is_featured: Boolean(jobState.formData.is_featured),
      approval_flow_id: jobState.formData.approval_flow_id || null,

      meta_title: jobState.formData.meta_title,
      meta_description: jobState.formData.meta_description,

      skills: jobState.skills.map((s) => ({
        skill_id: s.skill_id,
        skill_name: s.skill_name,
        is_required: Boolean(s.is_required),
        minimum_years: Number(s.minimum_years || 0),
        proficiency_level: s.proficiency_level,
      })),
    };
  };

  const hideConfirmation = () => {
    jobDispatch({ type: "HIDE_CONFIRMATION" });
  };
  const handleEditJob = async (job: Job) => {
    try {
      const resp = await getJobsDetails(job.id);
      const jobDetails = resp.data;

      jobDispatch({
        type: "SET_EDITING_JOB",
        payload: jobDetails,
      });

      jobDispatch({
        type: "PREFILL_FORM",
        payload: jobDetails,
      });

      jobDispatch(setShowModal(true));
    } catch (error) {
      console.error(error);
      showToastMessage("Failed to load job details", "error");
    } finally {
      // //jobDispatch(setLoading(false));
    }
  };

  const handleCreateFromDraft = async (job: Job) => {
    try {
      const resp = await getJobsDetails(job.id);
      const jobDetails = resp.data;
      jobDispatch({ type: "SET_EDITING_JOB", payload: jobDetails });
      jobDispatch({ type: "PREFILL_FORM", payload: jobDetails });
      jobDispatch({ type: "SET_CREATE_FROM_DRAFT", payload: true });
      jobDispatch(setShowModal(true));
    } catch (error) {
      console.error(error);
      showToastMessage("Failed to load job details", "error");
    }
  };
  const handleSaveDraft = async () => {
    try {
      const errors: any = {};
      const title = jobState.formData.title?.trim();
      if (!title) errors.title = "Title is required";
      else if (title.length < 3)
        errors.title = "Title must be at least 3 characters";
      if (!jobState.formData.posting_visibility)
        errors.posting_visibility = "Posting visibility is required";
      if (Object.keys(errors).length > 0) {
        jobDispatch(setFormErrors(errors));
        return;
      }

      await createJob({ ...buildJobPayload() }, true);
      showToastMessage("Draft saved successfully", "success");
      await fetchJobs();
      jobDispatch({ type: "SET_SHOW_MODAL", payload: false });
      jobDispatch({ type: "RESET_FORM" });
    } catch (error: any) {
      console.error(error);
      showToastMessage(
        error?.response?.data?.message || "Failed to save draft",
        "error",
      );
    }
  };

  const handleUpdateDraft = async () => {
    if (!jobState.editingJob) return;
    try {
      const errors: any = {};
      const title = jobState.formData.title?.trim();
      if (!title) errors.title = "Title is required";
      else if (title.length < 3)
        errors.title = "Title must be at least 3 characters";
      if (!jobState.formData.posting_visibility)
        errors.posting_visibility = "Posting visibility is required";
      if (Object.keys(errors).length > 0) {
        jobDispatch(setFormErrors(errors));
        return;
      }

      await updateJobData(jobState.editingJob.id, {
        ...buildJobPayload(),
      });
      showToastMessage("Draft updated successfully", "success");
      await fetchJobs();
      jobDispatch({ type: "SET_SHOW_MODAL", payload: false });
      jobDispatch({ type: "RESET_FORM" });
    } catch (error: any) {
      console.error(error);
      showToastMessage(
        error?.response?.data?.message || "Failed to update draft",
        "error",
      );
    }
  };

  const today = new Date().toISOString().split("T")[0];

  const handleSubmitJob = async (createFromDraft = false) => {
    const errors = validateJobForm(jobState.formData);
    if (Object.keys(errors).length > 0) {
      jobDispatch(setFormErrors(errors));
      return;
    }
    jobDispatch(setFormErrors({}));

    try {
      let jobId = "";

      if (
        jobState.editingJob &&
        !jobState.isCreateFromDraft &&
        !createFromDraft
      ) {
        await updateJobData(jobState.editingJob.id, buildJobPayload());
        jobId = jobState.editingJob.id;
      } else {
        const response = await createJob({
          ...buildJobPayload(),
        });
        jobId = response.data.id;
      }

      const defaultFlow = jobState.approvalFlows.find((f) => f.is_default) ?? jobState.approvalFlows[0];

      jobDispatch({
        type: "SHOW_APPROVAL_POPUP",
        payload: { jobId, flowId: defaultFlow?.id ?? "" },
      });

      jobDispatch({ type: "SET_SHOW_MODAL", payload: false });
      jobDispatch({ type: "RESET_FORM" });
    } catch (error: any) {
      console.error(error);
      showToastMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to submit job",
        "error",
      );
    }
  };

  const resetForm = () => {};

  useEffect(() => {
    // ONLY set default hiring manager for CREATE mode
    if (
      !jobState.editingJob &&
      jobState.users.length > 0 &&
      currentUser?.id &&
      !jobState.formData.hiring_manager_name
    ) {
      jobDispatch({
        type: "UPDATE_FORM_FIELD",
        payload: {
          field: "hiring_manager_name",
          value: currentUser.id,
        },
      });
    }
  }, [jobState.users, currentUser, jobState.editingJob]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Job Management</h1>
            <p className="mt-2 text-gray-600">
              Create and manage job postings, track applications, and find
              matching candidates
            </p>
          </div>

          {jobState.activeTab === "jobs" && (
            <button
              onClick={() => {
                jobDispatch({ type: "SET_SHOW_MODAL", payload: true });

                if (currentUser?.id) {
                  jobDispatch(
                    updateFormField("hiring_manager_name", currentUser.id),
                  );
                }
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              + Create Job
            </button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() =>
                jobDispatch({ type: "SET_ACTIVE_TAB", payload: "jobs" })
              }
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                jobState.activeTab === "jobs"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">💼</span>
                <div className="text-left">
                  <div className="flex items-center space-x-2">
                    <span>Job Postings</span>
                  </div>
                  <div className="text-xs text-gray-500 font-normal">
                    Create and manage job listings
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() =>
                jobDispatch({
                  type: "SET_ACTIVE_TAB",
                  payload: "pendingApprovals",
                })
              }
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                jobState.activeTab === "pendingApprovals"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">📋</span>
                <div className="text-left">
                  <div className="flex items-center space-x-2">
                    <span>Pending Approvals</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {/* {jobs.reduce(
                        (sum, job) => sum + job.applications_count,
                        0
                      )} */}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 font-normal">
                    Manage pending Approvals
                  </div>
                </div>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {jobState.activeTab === "jobs" && (
        <JobPosting
          onEditJob={handleEditJob}
          onCreateFromDraft={handleCreateFromDraft}
        />
      )}
      {/* Applications Tab Content */}
      {/* {jobState.activeTab === "applications" && <ApplicationManagement />} */}
      {jobState.activeTab === "pendingApprovals" && <PendingApprovalsPage />}

      {/* Job Form Modal */}
      <div>
        {jobState.showModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="w-full max-w-5xl bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between mb-2 px-6 py-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {jobState.editingJob ? "Edit Job" : "Create New Job"}
                </h3>
                <button
                  onClick={() => {
                    setShowModalValue(false);
                    // handleCloseModal();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {/* <XMarkIcon className="h-6 w-6" /> */}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitJob();
                  }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <InputField
                        required
                        label="Job Title"
                        id="title"
                        type="text"
                        placeholder="Enter job title"
                        value={jobState.formData.title || ""}
                        onChange={(e) => {
                          handleUpdateFormField("title", e.target.value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "title",
                          });
                        }}
                        error={jobState.validateErrors.title}
                      ></InputField>
                    </div>

                    <div>
                      <InputField
                        label="Department"
                        id="department"
                        type="text"
                        placeholder="Enter department"
                        required
                        value={jobState.formData.department || ""}
                        error={jobState.validateErrors.department}
                        onChange={(e) => {
                          handleUpdateFormField("department", e.target.value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "department",
                          });
                        }}
                      ></InputField>
                    </div>
                    <div>
                      <SelectdDropDown
                        id="recruiter_name"
                        label="Recruiter"
                        required
                        value={jobState.formData.recruiter_name || ""}
                        placeholder="Select Recruiter"
                        options={state.users
                          .filter((u) => u.role?.toLowerCase() === "recruiter")
                          .map((u) => ({
                            label: u.full_name,
                            value: u.id,
                          }))}
                        onChange={(value) => {
                          handleUpdateFormField("recruiter_name", value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "recruiter_name",
                          });
                        }}
                        error={jobState.validateErrors.recruiter_name}
                      />
                    </div>
                    <div>
                      <SelectdDropDown
                        id="hiring_manager_name"
                        label="Hiring Manager"
                        required
                        value={jobState.formData.hiring_manager_name || ""}
                        options={state.users.map((u) => ({
                          label: u.full_name,
                          value: u.id,
                        }))}
                        onChange={(value) => {
                          handleUpdateFormField("hiring_manager_name", value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "hiring_manager_name",
                          });
                        }}
                        error={jobState.validateErrors.hiring_manager_name}
                      />
                    </div>
                    <div>
                      <InputField
                        label="Location"
                        id="location"
                        required
                        placeholder="Enter location"
                        value={jobState.formData.location || ""}
                        onChange={(e) => {
                          handleUpdateFormField("location", e.target.value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "location",
                          });
                        }}
                        error={jobState.validateErrors.location}
                      ></InputField>
                    </div>

                    <div>
                      <SelectdDropDown
                        id="type"
                        label=" Job Type"
                        placeholder="Job type"
                        required
                        value={jobState.formData.job_type || ""}
                        options={Object.values(JobType).map((type) => ({
                          label: JobTypeLabelMap[type],
                          value: type,
                        }))}
                        onChange={(value) => {
                          handleUpdateFormField("job_type", value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "job_type",
                          });
                        }}
                        error={jobState.validateErrors.job_type}
                      />
                    </div>

                    <div>
                      <SelectdDropDown
                        id="experience_level"
                        label="Experience Level"
                        placeholder="Experience Level"
                        value={jobState.formData.experience_level || ""}
                        options={Object.values(ExperienceLevel).map((type) => ({
                          label: ExperienceLeveMap[type],
                          value: type,
                        }))}
                        onChange={(value) => {
                          handleUpdateFormField("experience_level", value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "experience_level",
                          });
                        }}
                      />
                    </div>
                    <div>
                      <SelectdDropDown
                        id="work_mode"
                        label=" Work mode"
                        placeholder="Work mode"
                        required
                        value={jobState.formData.work_mode || ""}
                        options={Object.values(WorkMode).map((type) => ({
                          label: WorkModeMap[type],
                          value: type,
                        }))}
                        onChange={(value) => {
                          handleUpdateFormField(
                            "work_mode" as keyof Job,
                            value,
                          );
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "work_mode",
                          });
                        }}
                        error={jobState.validateErrors.work_mode}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputField
                        min={0}
                        label=" Minimum Salary"
                        type="number"
                        id="salary_min"
                        value={jobState.formData.salary_min || ""}
                        onChange={(e) => {
                          handleUpdateFormField("salary_min", e.target.value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "salary_min",
                          });
                        }}
                        error={jobState.validateErrors.salary_min}
                        placeholder="e.g., 80000"
                      ></InputField>
                    </div>

                    <div>
                      <InputField
                        min={0}
                        label=" Maximum Salary"
                        type="number"
                        id="salary_max"
                        error={jobState.validateErrors.salary_max}
                        value={jobState.formData.salary_max || ""}
                        onChange={(e) => {
                          handleUpdateFormField("salary_max", e.target.value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "salary_max",
                          });
                        }}
                        placeholder="e.g., 120000"
                      ></InputField>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputField
                        label="Job Function"
                        id="job_function"
                        placeholder="Enter job function"
                        value={jobState.formData.job_function || ""}
                        onChange={(e) => {
                          handleUpdateFormField("job_function", e.target.value);
                        }}
                      ></InputField>
                    </div>
                    <div>
                      <SelectdDropDown
                        id="education_level"
                        label="Education Level"
                        placeholder="Education Level "
                        value={jobState.formData.education_level || ""}
                        options={Object.values(EducationLevel).map((type) => ({
                          label: EducationLevelMap[type],
                          value: type,
                        }))}
                        onChange={(value) => {
                          handleUpdateFormField("education_level", value);
                        }}
                      />
                    </div>
                  </div>

                  {/* SaleryPeriod */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <SelectdDropDown
                        id="salary_currency"
                        label=" Salary Currency"
                        value={jobState.formData.salary_currency || ""}
                        placeholder="Select Currency"
                        options={Object.values(salaryCurrency).map(
                          (currency) => ({
                            label: currency,
                            value: currency,
                          }),
                        )}
                        onChange={(value) =>
                          handleUpdateFormField("salary_currency", value)
                        }
                      />
                    </div>
                    <div>
                      <SelectdDropDown
                        id="salary_period"
                        label="Salary Period"
                        value={jobState.formData.salary_period || ""}
                        placeholder="Select Period "
                        options={Object.values(SalaryPeriod).map((period) => ({
                          label: SalaryPeriodMap[period],
                          value: period,
                        }))}
                        onChange={(value) =>
                          handleUpdateFormField("salary_period", value)
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputField
                        label="vacancies"
                        id="vacancies"
                        type="number"
                        min={0}
                        value={jobState.formData.vacancies}
                        onChange={(e) =>
                          handleUpdateFormField(
                            "vacancies",
                            parseInt(e.target.value),
                          )
                        }
                      ></InputField>
                    </div>
                    <div>
                      <InputField
                        label="Travel Percentage"
                        id="travel_percentage"
                        type="number"
                        min={0}
                        value={jobState.formData.travel_percentage}
                        onChange={(e) =>
                          handleUpdateFormField(
                            "travel_percentage",
                            parseInt(e.target.value),
                          )
                        }
                      ></InputField>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <SelectdDropDown
                        id="visa_sponsorship"
                        label="Visa Sponsorship"
                        value={
                          jobState.formData.visa_sponsorship === true
                            ? "yes"
                            : jobState.formData.visa_sponsorship === false
                            ? "no"
                            : ""
                        }
                        options={[
                          { label: "Not specified", value: "" },
                          { label: "Yes", value: "yes" },
                          { label: "No", value: "no" },
                        ]}
                        onChange={(value) =>
                          handleUpdateFormField(
                            "visa_sponsorship",
                            value === "" ? null : value === "yes",
                          )
                        }
                      />
                    </div>
                    <div>
                      <SelectdDropDown
                        id="relocation_offered"
                        label="Relocation Offered"
                        value={
                          jobState.formData.relocation_offered === true
                            ? "yes"
                            : jobState.formData.relocation_offered === false
                            ? "no"
                            : ""
                        }
                        options={[
                          { label: "Not specified", value: "" },
                          { label: "Yes", value: "yes" },
                          { label: "No", value: "no" },
                        ]}
                        onChange={(value) =>
                          handleUpdateFormField(
                            "relocation_offered",
                            value === "" ? null : value === "yes",
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputField
                        label="Target Start Date"
                        id="target_start_date"
                        type="date"
                        value={jobState.formData.target_start_date || ""}
                        onChange={(e) =>
                          handleUpdateFormField(
                            "target_start_date",
                            e.target.value,
                          )
                        }
                      ></InputField>
                    </div>
                    <div>
                      <SelectdDropDown
                        id="hiring_urgency"
                        label="Hiring Urgency "
                        required
                        placeholder="Select Hiring Urgency"
                        value={jobState.formData.hiring_urgency || ""}
                        options={Object.values(HiringUrgency).map(
                          (hiringUrgency) => ({
                            label: HiringUrgencyMap[hiringUrgency],
                            value: hiringUrgency,
                          }),
                        )}
                        onChange={(value) =>
                          handleUpdateFormField("hiring_urgency", value)
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputField
                        label="Interview Process"
                        id="interview_process"
                        value={jobState.formData.interview_process || ""}
                        onChange={(e) =>
                          handleUpdateFormField(
                            "interview_process",
                            e.target.value,
                          )
                        }
                      ></InputField>
                    </div>
                    <div>
                      <InputField
                        label="Application Deadline"
                        id="application_deadline"
                        type="date"
                        min={today}
                        value={jobState.formData.application_deadline || ""}
                        onChange={(e) =>
                          handleUpdateFormField(
                            "application_deadline",
                            e.target.value,
                          )
                        }
                      ></InputField>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputField
                        label="Max Applications"
                        id="max_applications"
                        min={1}
                        type="number"
                        value={jobState.formData.max_applications}
                        onChange={(e) =>
                          handleUpdateFormField(
                            "max_applications",
                            parseInt(e.target.value),
                          )
                        }
                      ></InputField>
                    </div>
                    <div>
                      <SelectdDropDown
                        id="posting_visibility"
                        label="Posting Visibility"
                        placeholder="Posting visibility"
                        required
                        value={jobState.formData.posting_visibility || ""}
                        options={Object.values(PostingVisibility).map(
                          (visibility) => ({
                            label: PostingVisibilityLabelMap[visibility],
                            value: visibility,
                          }),
                        )}
                        onChange={(value) => {
                          handleUpdateFormField("posting_visibility", value);
                          jobDispatch({
                            type: "CLEAR_FORM_ERROR",
                            payload: "posting_visibility",
                          });
                        }}
                        error={jobState.validateErrors.posting_visibility}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InputField
                        label=" Meta Title"
                        id="meta_title"
                        placeholder="Enter meta title"
                        value={jobState.formData.meta_title || ""}
                        onChange={(e) =>
                          handleUpdateFormField("meta_title", e.target.value)
                        }
                      ></InputField>
                    </div>
                    <div>
                      <InputField
                        label=" Meta Description"
                        id="meta_description"
                        placeholder="Enter meta description"
                        value={jobState.formData.meta_description || ""}
                        onChange={(e) =>
                          handleUpdateFormField(
                            "meta_description",
                            e.target.value,
                          )
                        }
                      ></InputField>
                    </div>
                  </div>
                  <div>
                    <InputField
                      label="Responsibilities"
                      id="responsibilities"
                      placeholder="Enter responsibilities"
                      value={jobState.formData.responsibilities || ""}
                      onChange={(e) =>
                        handleUpdateFormField(
                          "responsibilities",
                          e.target.value,
                        )
                      }
                    ></InputField>
                  </div>
                  <div>
                    <TextAreaField
                      id="benefits"
                      label="Benefits"
                      rows={4}
                      value={jobState.formData.benefits || ""}
                      placeholder="Company benefits..."
                      onChange={(e) =>
                        handleUpdateFormField("benefits", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <TextAreaField
                      id="description"
                      label="Job Description"
                      required
                      rows={6}
                      value={jobState.formData.description || ""}
                      placeholder="Describe the role, responsibilities, and expectations..."
                      error={jobState.validateErrors.description}
                      onChange={(e) => {
                        handleUpdateFormField("description", e.target.value);

                        jobDispatch({
                          type: "CLEAR_FORM_ERROR",
                          payload: "description",
                        });
                      }}
                    />
                  </div>

                  <div>
                    <TextAreaField
                      id="requirements"
                      label="Requirements (one per line)"
                      rows={4}
                      value={jobState.formData.requirements || ""}
                      placeholder="5+ years of experience&#10;Bachelor's degree in Computer Science&#10;Strong communication skills"
                      onChange={(e) => {
                        handleUpdateFormField("requirements", e.target.value);
                      }}
                    />
                  </div>
                  <SkillsSection
                    skills={jobState.skills}
                    newSkill={jobState.newSkill}
                    onAddSkill={handleAddSkill}
                    onDeleteSkill={handleDeleteSkill}
                    onNewSkillChange={(skill) =>
                      jobDispatch(setNewSkill(skill))
                    }
                    onUpdateSkill={handleUpdateSkill}
                  />
                </form>
              </div>
              <div className="sticky bottom-0 bg-white p-4 border-t flex justify-end gap-2">
                <div className="flex items-center justify-between w-full pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModalValue(false);
                      jobDispatch({ type: "RESET_FORM" });
                      jobDispatch({
                        type: "SET_CREATE_FROM_DRAFT",
                        payload: false,
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>

                  <div className="flex items-center space-x-3">
                    {/* CREATE MODE: Draft + Submit Job */}
                    {!jobState.editingJob && (
                      <>
                        <button
                          onClick={handleSaveDraft}
                          type="button"
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Draft
                        </button>
                        <button
                          onClick={() => handleSubmitJob()}
                          type="button"
                          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                        >
                          Submit Job
                        </button>
                      </>
                    )}
                    {/* EDIT DRAFT MODE: Update Draft + Create Job */}
                    {jobState.editingJob &&
                      jobState.editingJob.status === JobStatus.Draft && (
                        <>
                          <button
                            onClick={handleUpdateDraft}
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                          >
                            Update Draft
                          </button>
                          <button
                            onClick={() => handleSubmitJob(true)}
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                          >
                            Create Job
                          </button>
                        </>
                      )}
                    {/* EDIT NON-DRAFT MODE: Update button only */}
                    {jobState.editingJob &&
                      jobState.editingJob.status !== JobStatus.Draft && (
                        <button
                          onClick={() => handleSubmitJob()}
                          type="button"
                          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                        >
                          Update
                        </button>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={jobState.confirmation.isOpen}
        title={jobState.confirmation.title}
        message={jobState.confirmation.message}
        onConfirm={jobState.confirmation.onConfirm}
        onClose={hideConfirmation}
        type={jobState.confirmation.type}
        loading={jobState.confirmation.loading}
      />

      {/* Toast Notification */}
      <Toast
        key={toastInstanceKey}
        message={jobState.toast.message}
        type={jobState.toast.type}
        isVisible={jobState.toast.isVisible}
        onClose={hideToastMessage}
      />
      <ApprovalPopup
        open={jobState.showApprovalPopup}
        approvalFlows={jobState.approvalFlows}
        selectedId={jobState.selectedApprovalFlowId}
        onChange={(id) =>
          jobDispatch({
            type: "SET_SELECTED_APPROVAL_FLOW",
            payload: String(id),
          })
        }
        onConfirm={async () => {
          await fetchSubmittedApprovals();
        }}
        onClose={() => jobDispatch({ type: "HIDE_APPROVAL_POPUP" })}
      />
    </div>
  );
};

export default JobManagement;
